import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import SequenceUtils from "Utils/SequenceUtils";
import { makeApi, URLS } from "Utils/apiURL";
import Button from "Components/common/Button";
import RadioButton from "Components/common/radioBtn";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { contactBulkStageChangeState } from "./ContactBulkStageChangeState";
import { toasterState } from "Components/common/toaster";
import ContactStageSkeleton from "./ContactStageSkeleton";

const BulkContactStageChangePopup = observer((props = {}) => {
  const { clearSelectedContacts = () => {}, reloadContactsPage = () => {} } =
    props || {};
  const {
    contactCount = 0,
    contactIds = [],
    allContactsSelectedInView = false
  } = contactBulkStageChangeState?.bulkStageUpdateObj || {};

  const [updateLoader, setUpdateLoader] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [contactStages, setContactStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(true);

  const fetchContactStages = async () => {
    setStagesLoading(true);
    const config = {
      url: URLS.getContactStage,
      method: "GET"
    };
    const response = await makeApi(config);
    if (+response?.status === 200 && response?.data?.length > 0) {
      setContactStages(response.data.map(item => ({ ...item, isChecked: false })));
    } else {
      setContactStages([]);
    }
    setStagesLoading(false);
  };

  useEffect(() => {
    fetchContactStages();
  }, []);

  const bulkStageChangeAction = async () => {
    if (!selectedStageId) return;
    setUpdateLoader(true);
    const response = await contactBulkStageChangeState.bulkStageChange(
      selectedStageId,
      allContactsSelectedInView ? [] : contactIds,
      allContactsSelectedInView,
      contactCount
    );
    const { status = "", data = {} } = response || {};
    const {
      background = false,
      activeJob = false,
      bulkOperationResult = {}
    } = data || {};
    const { success = 0, failed = 0 } = bulkOperationResult || {};
    if (+status === 200) {
      if (activeJob) {
        SequenceUtils.showActiveJobPopupForBulkActions();
      } else if (background) {
        confirmationPopupState.setPopupValues({
          content:
            "Contacts are currently being updated. This process may take a few to several minutes, depending on the total number of contacts selected.",
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        reloadContactsPage();
      } else if (success > 0) {
        confirmationPopupState.setPopupValues({
          content: `${success} contact${success > 1 ? "s" : ""} ${
            success > 1 ? "have" : "has"
          } been updated successfully`,
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        reloadContactsPage();
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to update ${failed} contact${failed > 1 ? "s" : ""}`,
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
      }
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
    clearSelectedContacts();
    contactBulkStageChangeState.setShowBulkUpdateStagePopup(false);
    contactBulkStageChangeState.resetBulkStageUpdateObj();
    setUpdateLoader(false);
  };

  const closePopup = () => {
    contactBulkStageChangeState.setShowBulkUpdateStagePopup(false);
    contactBulkStageChangeState.resetBulkStageUpdateObj();
  };

  const radioOnchangeAction = (e, id = "") => {
    setContactStages(prev =>
      prev.map(item => ({ ...item, isChecked: id === item?.id }))
    );
    setSelectedStageId(id);
  };

  const getSelectedCount = () => {
    return allContactsSelectedInView ? "All" : contactCount;
  };

  const buttonList = [
    {
      id: "cancel",
      name: "cancel",
      btnText: "Cancel",
      type: "button",
      className: "cancelButton",
      click: closePopup
    },
    {
      id: "update",
      name: "update",
      btnText: "Update",
      type: "button",
      className: `updateButton ${updateLoader ? "loading" : ""}`,
      click: bulkStageChangeAction,
      loader: updateLoader,
      disabled: !selectedStageId || stagesLoading
    }
  ];

  return (
    <div className="bulkContactStageChangeComp">
      <div className="card animated customzoomIn">
        <div className="cardHeader">
          <div className="titleContainer">
            <div className="title">Update Contact Stage</div>
            <div className="contactSelectedCont">{`${getSelectedCount()} contacts selected`}</div>
          </div>
          <span className="closeIcon">
            <i className="material-icons close" onClick={closePopup}>
              close
            </i>
          </span>
        </div>
        <div className="cardBody">
          <div className="contactStageListCont">
            {stagesLoading
              ? <ContactStageSkeleton />
              : contactStages?.map(item => {
                  const {
                    displayName = "",
                    id = "",
                    isChecked = false
                  } = item || {};
                  return (
                    <RadioButton
                      id={id}
                      value={displayName}
                      name="contactStage"
                      defaultChecked={isChecked}
                      radioOnchange={radioOnchangeAction}
                      key={id}
                    />
                  );
                })}
          </div>
          <div className="buttonActionList">
            {buttonList.map(item => (
              <Button {...item} key={`${item?.id}Btn`} />
            ))}
          </div>
        </div>
      </div>
      <div className="bgOverlay" />
    </div>
  );
});

export default BulkContactStageChangePopup;
export { BulkContactStageChangePopup };
